import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Check, Handshake } from 'lucide-react';
import { SectionHeader, CtaBand } from '@/components/brand/BrandKit';
import { SimpleLeadForm } from '@/components/forms/SimpleLeadForm';
import { PARTNERSHIPS, PARTNERSHIP_ORDER, COLLABORATE_HERO, COLLABORATE_DISCLOSURE } from '@/data/partnerships';

const WHY_PARTNER = [
  "Access a broader mobility ecosystem across self-drive, managed transport and fleet solutions",
  "Explore partnership models built around real operational and customer needs",
  "Work with a UAE-focused group supporting both business and consumer mobility journeys",
  "Create more seamless transport and service experiences for your customers, guests, teams or operations",
];

const OPPORTUNITIES = [
  "Guest pickup, drop-off and airport-transfer support for hospitality partners",
  "Private and managed transport for events, launches, exhibitions and MICE requirements",
  "Business mobility and fleet support for operational teams and commercial users",
  "Property-tour and site-linked transport support for real estate businesses",
  "Fleet and distribution support for sectors with business-use vehicle requirements",
  "Automotive and vehicle-related collaboration for relevant service businesses",
];

export default function CollaboratePage() {
  useEffect(() => { document.title = "Collaborate With Us | Partnerships | Eurogulf Mobility Group"; }, []);

  return (
    <div data-testid="collaborate-page">
      <section data-testid="collaborate-hero" className="relative overflow-hidden bg-[#121212] text-white">
        <img src="/brand/fleet-tech-map.jpg" alt="Eurogulf Mobility Group partnerships" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32">
          <span className="chip-orange">Collaborate With Us</span>
          <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-white max-w-4xl">{COLLABORATE_HERO.headline}</h1>
          <p className="mt-6 font-body text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">{COLLABORATE_HERO.supporting}</p>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <a href="#enquiry" className="btn-primary">Start a Partnership <ArrowRight className="w-4 h-4" /></a>
            <a href="#pathways" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#121212]">Explore Pathways</a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24 border-b border-black/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader chip="Why Collaborate" title="The right partnership creates value on both sides." desc="We collaborate with businesses that need trusted mobility support, transport capability, fleet solutions, guest-service movement or tailored commercial programmes aligned to their operating model." />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {WHY_PARTNER.map((item) => (
              <div key={item} className="flex items-start gap-3 card-light p-5">
                <Check className="w-5 h-5 text-[#EE5A01] mt-0.5 flex-shrink-0" />
                <p className="font-body text-sm text-[#333] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pathways" className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader chip="Partnership Pathways" title="Find the collaboration that fits your business." desc="Every pathway is built around real operational and customer needs, with product combinations and integrations tailored to your model." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PARTNERSHIP_ORDER.map((slug) => {
              const p = PARTNERSHIPS[slug];
              return (
                <Link key={slug} to={`/collaborate/${slug}`} data-testid={`pathway-card-${slug}`} className="card-light group p-6 flex flex-col">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#EE5A01] text-white"><p.icon className="w-6 h-6" /></span>
                  <h3 className="mt-5 font-heading font-bold text-lg text-[#121212] group-hover:text-[#EE5A01] transition-colors">{p.title}</h3>
                  <p className="mt-2 font-body text-sm text-[#666666] leading-relaxed flex-1">{p.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1 font-heading font-bold text-xs text-[#EE5A01] group-hover:gap-2 transition-all">Explore <ArrowRight className="w-3.5 h-3.5" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="collaborate-opportunities" className="bg-[#FAFAFA] py-16 sm:py-24 border-y border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader chip="Example Opportunities" title="Ways we already work with partners." align="center" desc="A snapshot of the collaboration models we support across sectors, from single-service referrals to group-wide programmes." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {OPPORTUNITIES.map((opp) => (
              <div key={opp} className="card-light p-5 flex items-start gap-3">
                <Handshake className="w-5 h-5 text-[#EE5A01] mt-0.5 flex-shrink-0" />
                <p className="font-body text-sm text-[#333] leading-relaxed">{opp}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 rounded-2xl border border-black/5 bg-[#FAFAFA] p-6">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#FFF4ED] text-[#EE5A01] flex-shrink-0"><ShieldCheck className="w-5 h-5" /></span>
            <p className="font-body text-sm text-[#555555] leading-relaxed">{COLLABORATE_DISCLOSURE}</p>
          </div>
        </div>
      </section>

      <section id="enquiry" className="bg-white py-16 sm:py-24 border-t border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <SectionHeader chip="Start the Conversation" title="Let us design a partnership around your goals." desc="Tell us who you serve and what you want to achieve. Our partnerships team will propose the right product combination and pathway." />
          </div>
          <div className="lg:col-span-6">
            <SimpleLeadForm enquiryType="Partnership: General Collaboration" source="Collaborate With Us" title="Partnership enquiry" testid="collaborate-lead-form" />
          </div>
        </div>
      </section>

      <CtaBand title="Ready to explore the possibilities?" desc="From a single referral link to a group-wide strategic programme, we will meet you where your business is." ctas={[{ label: 'Contact Our Team', href: '/contact', testid: 'collaborate-cta' }, { label: 'Call 800 364', href: 'tel:800364', external: true, variant: 'outline' }]} />
    </div>
  );
}
